import React from "react";
import {
  FileText,
  Clock,
  Truck,
  CheckCircle,
  RotateCcw,
  RefreshCcw,
  Package,
  DollarSign,
  Box,
  ShoppingCart,
} from "lucide-react";
import { INVENTORY_SUB_TABS } from "@/constants/globalConstants";

import { useInventoryLogic } from "./hooks/useInventoryLogic";
import { fmt } from "@/lib/formatters";
import { HeroBanner, NewMainTabs, NewSubTabs } from "@/components/ui";
import { Settings as SettingsIcon } from "lucide-react";
import { InventoryStats } from "../../components/InventoryStats";
import { InventoryTabs } from "../../components/InventoryTabs";
import { InventoryFormModal } from "../../components/InventoryFormModal";
import { RestockModal } from "../../components/RestockModal";
import { RestockReturnModal } from "../../components/RestockReturnModal";
import { MaterialRequestDetailModal } from "../../components/MaterialRequestDetailModal";
import { MaterialReturnDetailModal } from "../../components/MaterialReturnDetailModal";
import { PurchaseReturnDetailModal } from "../../components/PurchaseReturnDetailModal";
import { DispatchModal } from "../../components/DispatchModal";
import { PurchaseReturnPaymentModal } from "../../components/PurchaseReturnPaymentModal";
import { PurchaseReturnForm } from "../../components/PurchaseReturnForm";
import { PurchaseEntryModal } from "../../components/PurchaseEntryModal";
import SalesForm from "../../components/SalesForm";
import { OpenStockEntryModal } from "../../components/OpenStockEntryModal";
import { PaymentDetailsModal } from "../../components/PaymentDetailsModal";
import { InventorySettings } from "../InventorySettings/index";

const RequestMaterialsModal = () => null;

// Modular Views
import { ItemsMasterView } from "../../components/ItemsMasterView";
import { PurchasesView } from "../../components/PurchasesView";
import { SalesView } from "../../components/SalesView";

import { OpenStockView } from "../../components/OpenStockView";
import { ReturnsView } from "../../components/ReturnsView";
import { RequestsView } from "../../components/RequestsView";
import { ItemDetailsModal } from "../../components/ItemDetailsContent";
import { OrderRequestsTable } from "../../components/order_requests_table";
import { downloadInventoryAsExcel, downloadPurchasesAsExcel, downloadReturnsAsExcel, downloadSalesAsExcel, downloadMaterialRequestsAsExcel } from "../../utils/exportUtils";

export const InventoryPage = () => {
  const [viewPaymentData, setViewPaymentData] = React.useState(null);
  const {
    user,
    inventory,
    activeInventory,
    materialRequests,
    filteredRequests,
    projects,
    activeTab,
    purchases,
    inventorySubTab,
    requestStatusOptions,
    setInventorySubTab,
    viewMode,
    setViewMode,
    search,
    setSearch,
    filters,
    setFilters,
    lowStock,
    noStockCount,
    filtered,
    totalValue,
    itemCount,
    totalUnits,
    averageValuePerItem,
    statsBreakdown,
    requestStats,
    activeReturnStats,
    activeReturnFilterOptions,
    activeSaleFilterOptions,
    purchaseStats,
    saleStats,
    openStockStats,
    isExpanded,
    setIsExpanded,
    addOpen,
    setAddOpen,
    restockItem,
    setRestockItem,
    restockQty,
    setRestockQty,
    restockReturn,
    setRestockReturn,
    form,
    setForm,
    selReq,
    setSelReq,
    selReturn,
    setSelReturn,
    selSale,
    setSelSale,
    selPR,
    setSelPR,
    selSR,
    setSelSR,
    selectedPRForPayment,
    setSelectedPRForPayment,
    initialReturnInvoice,
    setInitialReturnInvoice,
    dispatchReq,
    setDispatchReq,
    addItem,
    handleNewPurchase,
    handleNewOpenStock,
    handleEditOpenStock,
    deleteItem,
    handleEdit,
    ownerAction,
    handleDispatch,
    handleDeliver,
    handleConfirmReturn,
    loading,
    viewItem,
    setViewItem,
    handleViewPurchases,
    dynamicSettings,
    editingItem,
    setEditingItem,
    sales, filteredSales, consolidatedSales, purchaseReturns, filteredPurchaseReturns, saleReturns, filteredSaleReturns, consolidatedReturns,
    purchaseReturnOpen,
    setPurchaseReturnOpen,
    saleReturnOpen,
    setSaleReturnOpen,
    openStockOpen,
    setOpenStockOpen,
    activeTab: activeTabValue, setActiveTab, subActiveTab, setSubActiveTab,
    openingStocks,
    returnsSubTab,
    setReturnsSubTab,
    allAggregatedItems,
    distributedMap,
    purchasedMap,
    handlePurchaseReturnPayment,
    handleSelectExistingItem,
    purchaseOpen,
    setPurchaseOpen,
    filteredPurchases,
    filteredOpenStock,
    saleOpen,
    setSaleOpen,
    finalRestockReturn,
    restock,
    getInventorySubTabs,
    addItemToList,
    removeItemFromList,
    editItemInList,
    refreshAll,
    submitting,
    rawInventory,
    masterInventory,
  } = useInventoryLogic();

  const [isRequestModalOpen, setIsRequestModalOpen] = React.useState(false);
  const [requestModalConfig, setRequestModalConfig] = React.useState({
    project: null,
    initialItems: {},
    initialDepartment: "",
  });

  const [isExporting, setIsExporting] = React.useState(false);

  const handleExportInventory = async () => {
    setIsExporting(true);
    // Export the filtered inventory array
    await downloadInventoryAsExcel(filtered);
    setIsExporting(false);
  };

  const handleExportPurchases = async () => {
    setIsExporting(true);
    // Export the filtered purchases array
    await downloadPurchasesAsExcel(filteredPurchases, dynamicSettings);
    setIsExporting(false);
  };

  const handleExportReturns = async (data = consolidatedReturns) => {
    setIsExporting(true);
    await downloadReturnsAsExcel(data, returnsSubTab === "damaged");
    setIsExporting(false);
  };

  const handleExportSales = async () => {
    setIsExporting(true);
    await downloadSalesAsExcel(consolidatedSales);
    setIsExporting(false);
  };

  const handleAddMaterialsToRequest = (req) => {
    // Map existing items to the format expected by RequestMaterialsModal
    const initialItems = {};
    req.items.forEach((it) => {
      // Robustly extract the ID string from the inventoryId object or fallback fields
      const purchaseLineId = it.purchaseLineId?._id || it.purchaseLineId?.id || it.purchaseLineId;
      const invId = it.inventoryId?._id || it.inventoryId?.id || it.inventoryId;
      const itemId = it.item?._id || it.item?.id || it.itemId;
      const id =
        (typeof purchaseLineId === "string" ? purchaseLineId : null) ||
        (purchaseLineId ? String(purchaseLineId) : null) ||
        (typeof invId === "string" ? invId : null) ||
        (typeof itemId === "string" ? itemId : null) ||
        it._id;

      if (id && typeof id === "string") {
        const requested = Number(it.qty || it.quantity || 0);
        const delivered = Number(it.deliveredQty || it.deliveredQuantity || 0);
        const remaining = requested - delivered;
        if (remaining > 0) {
          initialItems[id] = remaining;
        }
      }
    });

    setRequestModalConfig({
      project:
        req.projectDetails || projects.find((p) => p._id === req.projectId),
      initialItems,
      existingItems: req.items || [],
      initialDepartment: req.installationTrade || "",
      requestId: req._id || req.id,
    });
    setIsRequestModalOpen(true);
  };

  const handleCreateNewRequest = () => {
    setRequestModalConfig({
      project: null,
      initialItems: {},
      initialDepartment: "",
      requestId: null,
    });
    setIsRequestModalOpen(true);
  };

  const renderInventoryContent = () => {
    switch (inventorySubTab) {
      case INVENTORY_SUB_TABS.ITEMS:
        return (
          <ItemsMasterView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              filtered,
              setRestockItem,
              handleEdit,
              handleViewPurchases,
              setViewItem,
              deleteItem,
              distributedMap,
              purchasedMap,
              onExport: handleExportInventory,
              isExporting,
              loading
            }}
          />
        );
      case INVENTORY_SUB_TABS.PURCHASES:
        return (
          <PurchasesView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              activeInventory,
              inventory,
              purchases: filteredPurchases,
              setInventorySubTab,
              handleNewPurchase,
              setViewItem,
              setRestockItem,
              setInitialReturnInvoice,
              setSelPR,
              setPurchaseReturnOpen,
              setEditingItem,
              setForm,
              setAddOpen,
              setPurchaseOpen,
              onExport: handleExportPurchases,
              isExporting
            }}
          />
        );
      case INVENTORY_SUB_TABS.SALES:
        return (
          <SalesView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              consolidatedSales,
              setSelReq,
              setSelSale,
              setSaleOpen,
              parties: activeSaleFilterOptions?.parties,
              onExport: handleExportSales,
              isExporting,
              onRefresh: refreshAll
            }}
          />
        );
      /* case INVENTORY_SUB_TABS.OPEN_STOCK:
        return (
          <OpenStockView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              openingStocks: filteredOpenStock,
              inventory,
              handleNewOpenStock,
              setViewItem,
              handleEditOpenStock,
              deleteItem,
            }}
          />
        ); */
      case "return_purchases":
      case "return_site":
      case "return_damaged":
        return (
          <ReturnsView
            {...{
              returnsSubTab: inventorySubTab.replace("return_", ""),
              setReturnsSubTab: (id) => setInventorySubTab("return_" + id),
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              activeReturnFilterOptions,
              consolidatedReturns,
              setSelReturn,
              setSelPR,
              setSelSR,
              setPurchaseReturnOpen,
              setSaleReturnOpen,
              handleConfirmReturn,
              setViewPaymentData,
              setSelectedPRForPayment,
              onExport: handleExportReturns,
              isExporting,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-full pb-2 overflow-x-hidden font-sans animate-in fade-in duration-500">
      <HeroBanner
        title="Inventory Management"
        subtitle="Space Designs Inventory Management"
        imageUrl="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1000&q=80"
      />

      <NewMainTabs
        tabs={[
          { id: 'requests', label: 'Order Requests', icon: <Package size={14} /> },
          { id: 'inventory', label: 'Add Material', icon: <Box size={14} /> },
          { id: 'settings', label: 'Settings', icon: <SettingsIcon size={14} /> }
        ]}
        activeTab={viewMode}
        onChange={setViewMode}
      />

      <div className="flex flex-col gap-4">
        {viewMode === "settings" && <InventorySettings />}

        {viewMode === "inventory" && (
          <div className="space-y-6">
            <InventoryStats
              activeTab={activeTab}
              viewMode={viewMode}
              subTab={inventorySubTab}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              inventory={masterInventory}
              sales={consolidatedSales}
              returns={consolidatedReturns}
              requests={materialRequests}
              purchaseReturns={[]}
              purchases={filteredPurchases}
              openingStocks={openingStocks}
            />

            <NewSubTabs
              tabs={getInventorySubTabs().map(t => ({ id: t.id, label: t.label }))}
              activeTab={inventorySubTab}
              onChange={(id) => {
                setInventorySubTab(id);
                setSearch("");
              }}
            />

            <div className="min-h-[400px]">{renderInventoryContent()}</div>
          </div>
        )}

        {viewMode === "requests" && (
          <div className="w-full h-full">
            <OrderRequestsTable search={search} filters={filters} setFilters={setFilters} />
          </div>
        )}

        {/* {viewMode === "returns" && (
          <ReturnsView
            {...{
              returnsSubTab,
              setReturnsSubTab,
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              activeReturnFilterOptions,
              consolidatedReturns,
              setSelReturn,
              setSelPR,
              setSelSR,
              setPurchaseReturnOpen,
              setSaleReturnOpen,
              handleConfirmReturn,
              setViewPaymentData,
              setSelectedPRForPayment,
              onExport: handleExportReturns,
              isExporting,
            }}
          />
        )} */}


      </div>
      {/* Modals */}
      <OpenStockEntryModal
        isOpen={openStockOpen}
        onClose={() => setOpenStockOpen(false)}
        form={form}
        setForm={setForm}
        onSubmit={addItem}
        onAddToList={addItemToList}
        onRemoveFromList={removeItemFromList}
        onEditItem={editItemInList}
        onSelectExistingItem={handleSelectExistingItem}
        inventory={inventory}
        dynamicSettings={dynamicSettings}
        masterItems={masterInventory}
        loading={loading}
      />
      <RestockModal
        restockItem={restockItem}
        onClose={() => setRestockItem(null)}
        restockQty={restockQty}
        setRestockQty={setRestockQty}
        onRestock={restock}
        submitting={submitting}
      />
      <RestockReturnModal
        restockReturn={restockReturn}
        onClose={() => setRestockReturn(null)}
        onRestock={finalRestockReturn}
      />
      <InventoryFormModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        activeTab={activeTab}
        onSubmit={addItem}
        dynamicSettings={dynamicSettings}
        isEditing={!!editingItem}
        inventory={inventory}
        onItemSelect={handleSelectExistingItem}
        initialData={form}
      />
      <MaterialRequestDetailModal
        selReq={selReq}
        onClose={() => setSelReq(null)}
        projects={projects}
        inventoryMaster={allAggregatedItems}
        user={user}
        onOwnerAction={ownerAction}
        onApproveRequest={setDispatchReq}
        isInventoryModule={true}
      />
      <MaterialReturnDetailModal
        selReturn={selReturn}
        onClose={() => setSelReturn(null)}
        user={user}
        onConfirmReceipt={handleConfirmReturn}
      />
      <PurchaseReturnDetailModal
        purchaseReturn={selPR}
        onClose={() => setSelPR(null)}
      />
      <PaymentDetailsModal
        isOpen={!!viewPaymentData}
        onClose={() => setViewPaymentData(null)}
        data={viewPaymentData}
      />
      <PurchaseReturnPaymentModal
        isOpen={!!selectedPRForPayment}
        onClose={() => setSelectedPRForPayment(null)}
        returnData={selectedPRForPayment}
        onConfirm={handlePurchaseReturnPayment}
      />
      <DispatchModal
        isOpen={!!dispatchReq}
        onClose={() => setDispatchReq(null)}
        requestId={dispatchReq?._id || dispatchReq?.id || dispatchReq}
        request={dispatchReq}
        onConfirm={(id, data) => handleDispatch(id, data)}
      />

      <PurchaseReturnForm
        isOpen={purchaseReturnOpen}
        onClose={() => setPurchaseReturnOpen(false)}
        initialInvoiceNo={initialReturnInvoice}
        dynamicSettings={dynamicSettings}
        serviceType={activeTab}
        onSuccess={() => {
          refreshAll();
        }}
      />
      <SalesForm
        isOpen={saleOpen}
        onClose={() => setSaleOpen(false)}
        onSaleSuccess={refreshAll}
        dynamicSettings={dynamicSettings}
        initialData={selSale}
        purchases={purchases}
      />
      <PurchaseEntryModal
        isOpen={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        form={form}
        setForm={setForm}
        onAddToList={addItemToList}
        onRemoveFromList={removeItemFromList}
        onEditItem={editItemInList}
        onSelectExistingItem={handleSelectExistingItem}
        onSubmit={addItem}
        dynamicSettings={dynamicSettings}
        inventory={inventory}
        masterItems={dynamicSettings.masterItems || []}
        loading={loading}
      />
      {/* View Item Modal */}
      <ItemDetailsModal item={viewItem} inventory={rawInventory} onClose={() => setViewItem(null)} />

      {isRequestModalOpen && (
        <RequestMaterialsModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          project={requestModalConfig.project}
          projects={projects}
          initialItems={requestModalConfig.initialItems}
          existingItems={requestModalConfig.existingItems || []}
          initialDepartment={requestModalConfig.initialDepartment}
          requestId={requestModalConfig.requestId}
          viewerRole={user?.role}
          source="inventory"
          onSuccess={() => {
            refreshAll();
            setIsRequestModalOpen(false);
          }}
        />
      )}
    </div>
  );
};
